/**
 * hiisi-currency.js — EUR-primary display with non-binding ECB estimate.
 *
 * Pole bills exclusively in EUR. Customer-side renderings on /pages/my-hiisi
 * and /pages/configurator show "€X" as the binding amount; for non-EUR
 * customers we append "≈<local>" using ECB mid-market rates fetched from
 * the Worker (/apps/hiisi-bespoke/fx-rates, KV-cached 24h, refreshed daily
 * by cron). This replaces the previous Shopify Markets cur_rate flow which
 * was unreliable in non-cart Liquid contexts.
 *
 * Liquid emits each amount as:
 *
 *   <span data-hiisi-eur="17738">€17,738</span>
 *
 * On DOMContentLoaded we read every such element, format the EUR primary
 * with Intl, and append the local estimate (when applicable). If JS is
 * disabled, scrapped, or fx-rates is offline AND the customer is non-EUR,
 * the customer simply sees the binding EUR figure — no broken conversion.
 *
 * The helper also exposes formatPrimary / formatEstimate / renderPrice for
 * imperative callers (the configurator's React tree calls these directly
 * to render its own price spans without going through the data-attribute
 * hydration path).
 */
(function () {
  'use strict';

  var FX_ENDPOINT = '/apps/hiisi-bespoke/fx-rates';

  // Last-resort baseline used only when the helper has to render an estimate
  // before fx-rates resolves (or after a failed fetch). Mirrors the same
  // table as fx-rates.ts FALLBACK_RATES so a customer never sees wildly
  // different numbers between server and client. Update annually.
  var FALLBACK_RATES = {
    USD: 1.09, GBP: 0.86, SEK: 10.5, NOK: 12.0, DKK: 7.45,
    CHF: 0.95, JPY: 165, CAD: 1.45, AUD: 1.65,
    PLN: 4.30, CZK: 25.0, HUF: 395, RON: 4.97, BGN: 1.96
  };

  var EUR_FORMATTER = null;
  function getEurFormatter() {
    if (EUR_FORMATTER) return EUR_FORMATTER;
    try {
      EUR_FORMATTER = new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
      });
    } catch (e) {
      EUR_FORMATTER = { format: function (n) { return '€' + Math.round(n); } };
    }
    return EUR_FORMATTER;
  }

  var localFormatterCache = {};
  function getLocalFormatter(iso) {
    if (localFormatterCache[iso]) return localFormatterCache[iso];
    try {
      // `undefined` locale = browser default (Accept-Language). Markets-iso
      // selects the unit; the reader's locale picks digit grouping + symbol
      // position. Matches the Shopify-cart formatter the customer has seen
      // elsewhere on the site, with no manual per-currency rules.
      localFormatterCache[iso] = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: iso,
        maximumFractionDigits: 0
      });
    } catch (e) {
      localFormatterCache[iso] = { format: function (n) { return iso + ' ' + Math.round(n); } };
    }
    return localFormatterCache[iso];
  }

  /**
   * Customer-side currency context. Reads, in order:
   *   1. window.HIISI_LOCALE_CTX.currencyIso  (set by my-hiisi section template)
   *   2. window.HIISI_CURRENCY.isoCode        (set by configurator section)
   *   3. <html data-hiisi-currency="...">     (last-resort opt-in for other pages)
   *   4. 'EUR'                                  (fallback — customer sees EUR-only)
   */
  function detectCustomerIso() {
    var ctx = window.HIISI_LOCALE_CTX;
    if (ctx && typeof ctx.currencyIso === 'string' && ctx.currencyIso) {
      return String(ctx.currencyIso).toUpperCase();
    }
    var legacy = window.HIISI_CURRENCY;
    if (legacy && typeof legacy.isoCode === 'string' && legacy.isoCode) {
      return String(legacy.isoCode).toUpperCase();
    }
    var attr = document.documentElement.getAttribute('data-hiisi-currency');
    if (attr) return attr.toUpperCase();
    return 'EUR';
  }

  var HiisiCurrency = {
    isoCode: 'EUR',
    rates: null,                 // Map<ISO, Number> once /fx-rates resolves
    fetchedAt: null,             // ISO timestamp from the Worker payload
    source: null,                // 'fresh' | 'cache' | 'baseline' (informational)
    initPromise: null,           // resolves once init() finishes (good or bad)

    /**
     * Fetch ECB rates (skip when EUR — saves an HTTP round-trip on the
     * majority storefront). Sets `this.rates` on success; on failure the
     * estimate output gracefully degrades to "no estimate" — primary EUR
     * still renders.
     */
    init: function () {
      if (this.initPromise) return this.initPromise;
      this.isoCode = detectCustomerIso();
      if (this.isoCode === 'EUR') {
        // No conversion needed — short-circuit so the page never blocks on
        // a network call it doesn't use.
        this.initPromise = Promise.resolve();
        return this.initPromise;
      }
      var self = this;
      this.initPromise = fetch(FX_ENDPOINT, { credentials: 'omit' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (data && data.ok && data.rates && typeof data.rates === 'object') {
            self.rates = data.rates;
            self.fetchedAt = data.date || null;
            self.source = data.source || 'remote';
          } else {
            self.rates = FALLBACK_RATES;
            self.source = 'fallback-client';
          }
        })
        .catch(function () {
          self.rates = FALLBACK_RATES;
          self.source = 'fallback-client';
        })
        .then(function () {
          // Notify subscribers (e.g. the configurator's React tree) that
          // formatEstimate() is now ready to return non-null strings. The
          // configurator listens for this and triggers a re-render so price
          // rows pick up the "≈local" tail without a manual refresh.
          try {
            document.dispatchEvent(new Event('hiisi-currency-ready'));
          } catch (e) { /* IE11-ish hosts: ignore — they wouldn't render this UI anyway */ }
        });
      return this.initPromise;
    },

    formatPrimary: function (eurAmount) {
      var n = Number(eurAmount);
      if (!Number.isFinite(n)) return '';
      return getEurFormatter().format(n);
    },

    /**
     * Returns the estimate string ("≈¥3,886,500"), or null when EUR or rate
     * unavailable. Caller decides whether to render it inline or skip.
     */
    formatEstimate: function (eurAmount) {
      if (this.isoCode === 'EUR') return null;
      if (!this.rates) return null;
      var rate = this.rates[this.isoCode];
      if (typeof rate !== 'number' || !(rate > 0)) return null;
      var n = Number(eurAmount);
      if (!Number.isFinite(n)) return null;
      return '≈' + getLocalFormatter(this.isoCode).format(n * rate);
    },

    /**
     * HTML string for inline rendering — primary EUR + optional estimate
     * span. Used both by the data-attribute hydration loop below and by
     * external callers that want a single string.
     */
    renderPrice: function (eurAmount) {
      var primary = this.formatPrimary(eurAmount);
      var estimate = this.formatEstimate(eurAmount);
      if (!estimate) return primary;
      // The estimate span gets a tooltip explaining the non-binding nature.
      // Title is plain English (no localisation): the customer is already on
      // an English storefront for non-EUR markets.
      return primary +
        ' <span class="hiisi-price-estimate" title="Estimated using ECB mid-market rate. Final billing in EUR.">' +
        estimate +
        '</span>';
    },

    /**
     * Walk the DOM and hydrate every [data-hiisi-eur] / [data-hiisi-eur-local]
     * element. Idempotent — stamps `data-hiisi-hydrated` on each touched node
     * so repeated calls (e.g. after dynamic content insertion) don't reformat
     * already-handled elements unless the caller explicitly clears the marker.
     *
     * Two cell types:
     *   1. [data-hiisi-eur]                — primary EUR. Default behaviour
     *      (every non-grid page) renders "€X" + inline "≈local" estimate.
     *      Grid layouts that have a separate local-estimate cell opt out of
     *      the inline estimate by adding `data-hiisi-no-estimate`.
     *   2. [data-hiisi-eur-local]          — separate local-estimate cell.
     *      Renders only "≈local" (or empty when EUR / rate unavailable).
     *      Used by my-hiisi-order-total.liquid's column grid.
     */
    hydrate: function (root) {
      var scope = root || document;

      var primary = scope.querySelectorAll('[data-hiisi-eur]:not([data-hiisi-hydrated])');
      for (var i = 0; i < primary.length; i++) {
        var el = primary[i];
        var eur = parseFloat(el.getAttribute('data-hiisi-eur'));
        if (!Number.isFinite(eur)) continue;
        if (el.hasAttribute('data-hiisi-no-estimate')) {
          // Grid-layout cell: render only the binding € amount; a sibling
          // [data-hiisi-eur-local] cell handles the estimate.
          el.textContent = this.formatPrimary(eur);
        } else {
          el.innerHTML = this.renderPrice(eur);
        }
        el.setAttribute('data-hiisi-hydrated', 'true');
      }

      var local = scope.querySelectorAll('[data-hiisi-eur-local]:not([data-hiisi-hydrated])');
      for (var j = 0; j < local.length; j++) {
        var lel = local[j];
        var leur = parseFloat(lel.getAttribute('data-hiisi-eur-local'));
        if (!Number.isFinite(leur)) continue;
        // formatEstimate returns null on EUR markets or before fx-rates resolve.
        // Empty string keeps the cell visually collapsed (no awkward "≈" alone)
        // — the EUR-only stylesheet hides the cell entirely anyway.
        var est = this.formatEstimate(leur);
        lel.textContent = est || '';
        lel.setAttribute('data-hiisi-hydrated', 'true');
      }
    }
  };

  window.HiisiCurrency = HiisiCurrency;

  // Hydrate after init resolves. Init is fast: EUR-customer = synchronous,
  // non-EUR = one fetch then hydrate. The static EUR primary is already in
  // the DOM (Liquid-rendered), so non-EUR customers see the binding amount
  // immediately and the "≈local" tail appears within ~100–500ms.
  function bootstrap() {
    HiisiCurrency.init().then(function () {
      HiisiCurrency.hydrate(document);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
